// Importing necessary modules and dependencies
import * as THREE from 'three'
import EventDispatcher from "../common/EventDispatcher";
import {
    Global
} from "../common/global";
import {
    isMobile,
    isMobileOnly
} from 'mobile-device-detect';
import gsap from 'gsap';

// Constants for gyro control
let newY;
let newX;
let sliderTouchIndex = -1;
const GYRO_THRESHOLD = 0.5;
const GYRO_FORCE_REDUCER = 7.5; //15;          // Reducing factor applied to gyro tilt for control sensitivity
const GYRO_FORCE_EXP = 4; //7.5;              // Exponential factor applied to the reduced gyro tilt for fine-tuning sensitivity
const GYRO_FACTOR = 1.5 // {{Explanation}}

// GYRO_FORCE_REDUCER: 
//   - This constant is used to reduce the raw gyro tilt value, influencing the sensitivity of gyro control.
//   - A higher value reduces sensitivity, making the gyro input less responsive.
//   - A lower value increases sensitivity, making the gyro input more responsive.
//   - Adjusting this value dynamically can fine-tune the responsiveness based on user preferences or gameplay balance.

// GYRO_FORCE_EXP: 
//   - This constant is an exponential factor applied to the reduced gyro tilt to further fine-tune sensitivity.
//   - It provides a non-linear adjustment, allowing for more control over the gyro input's impact on the game.
//   - Adjusting this value allows for precise tuning of gyro control sensitivity based on specific requirements.

// These constants play a crucial role in controlling the responsiveness of the gyroscopic input and can be adjusted
// to achieve the desired feel and balance in the game.

// Constants for joystick control
const FORCE_X_LIMIT = 0.0025 * 4;
const FORCE_X_FACT = 0.000025 * 4;
const FORCE_Y_U_FACT = -0.0005;
const FORCE_Y_D_FACT = 0.001;
const FORCE_X_LERP = 0.25;


let previousCompassDirection = null;

// GameControl class definition
export default class GameControl {
    constructor(scene) {
        this.scene = scene;

        // Event listeners for keyboard input
        document.addEventListener('keydown', this.onKeyDown.bind(this));
        document.addEventListener('keyup', this.onKeyUp.bind(this));

        // Binding handleOrientationChange function
        this.handleOrientationChange = this.handleOrientationChange.bind(this);
        this.applyAccelarator = this.applyAccelarator.bind(this);
        this.removeAccelarator = this.removeAccelarator.bind(this);
        this.applyBrake = this.applyBrake.bind(this);
        this.removeBrake = this.removeBrake.bind(this);

    }

    // Initialization method
    init() {
        // Initialize variables and flags
        this.prevBeta = 0;
        this.draggableCircle = null;
        this.sliderContainer = null;
        this.isDragging = false;
        this.directions = [];
        this.gameOver = false;
        Global.gameOver = false;
        this.gameStarted = false;
        this.forceX = 0;
        this.lastForceX = 0;
        this.initDirectionsSaved = false;
        this.readyToSaveDirection = false;
        this.initialOrientation = {
            beta: null
        };

        // Get EventDispatcher instance
        this.events = EventDispatcher.getObj();
        this.events.addEventListener("GAMESCENE_EVENTS", this.onEvents.bind(this));
        this.events.addEventListener("GAME_EVENTS", this.onGameEvents.bind(this));
        this.events.addEventListener("LOADER_EVENTS", this.onGameEvents.bind(this));

        // Show start button for mobile users
        if (isMobile) {

        }
        // document.querySelector("#gameStart").classList.add("active");


    }

    // Handle device orientation change for gyro control
    handleOrientationChange(event) {
        if (Global.controlType !== "gyro") return false;
        // Check if initial orientation is saved and ready to save
        if (!this.initDirectionsSaved && this.readyToSaveDirection && event.beta !== 0) {
            this.initDirectionsSaved = true;
            this.initialOrientation.beta = event.beta;
        }

        // Calculate the difference in beta (tilt) from the initial orientation
        let currentBeta = event.beta;
        if (this.initialOrientation.beta === null) {
            return;
        }
        const diffBeta = currentBeta - this.initialOrientation.beta;
        let _beta = diffBeta;


        let tiltDirection = Math.sign(event.gamma);
        // Compare beta values and dispatch gyro control events
        if (Math.abs(_beta) < GYRO_THRESHOLD || Math.abs(this.prevBeta) - 2 > Math.abs(currentBeta)) {
            this.prevBeta = currentBeta;
            this.events.dispatchEvent({
                type: "GAME_EVENTS",
                message: {
                    "event_type": "gyro_control_remove",
                    data: {
                        "code": "ArrowLeft"
                    }
                }
            });
            this.events.dispatchEvent({
                type: "GAME_EVENTS",
                message: {
                    "event_type": "gyro_control_remove",
                    data: {
                        "code": "ArrowRight"
                    }
                }
            });
        } else if (Math.abs(_beta) >= GYRO_THRESHOLD) {
            this.events.dispatchEvent({
                type: "GAME_EVENTS",
                message: {
                    "event_type": "gyro_control_add",
                    data: {
                        "code": Math.sign(_beta) === tiltDirection ? "ArrowLeft" : "ArrowRight",
                        "force": Math.min(1.25, Math.min(Math.pow(Math.abs(_beta) / GYRO_FORCE_REDUCER, GYRO_FORCE_EXP), Math.abs(_beta) / 10) / Global.delta)

                    }
                }
            });
            this.events.dispatchEvent({
                type: "GAME_EVENTS",
                message: {
                    "event_type": "gyro_control_remove",
                    data: {
                        "code": Math.sign(_beta) === -1 * tiltDirection ? "ArrowLeft" : "ArrowRight"
                    }
                }
            });
        }

        // Update prevBeta and dispatch start game event
        if (Math.abs(this.prevBeta) < Math.abs(currentBeta)) {
            this.prevBeta = currentBeta;
        }
        if (Math.sign(this.prevBeta) != Math.sign(currentBeta)) {
            this.prevBeta = 0;
        }
        // this.prevBeta = currentBeta;

    }

    // Start dragging event for joystick control
    startDragging(isCircle, e) {
        e.preventDefault();

        this.isDragging = true;

        sliderTouchIndex = e.touches.length;
        if (!isCircle) {
            const sliderRect = this.sliderContainer.getBoundingClientRect();
            if (Global.controlType == "gyro") {
                let initialY;
                if (e.type === 'touchstart') {
                    initialY = e.touches[0].clientY - sliderRect.top;
                } else {
                    initialY = e.clientY - sliderRect.top;
                }

                // Set the initial position of the draggable circle
                this.draggableCircle.style.top = `${initialY}px`;
            } else {
                let initialX;
                if (e.type === 'touchstart') {
                    initialX = e.touches[0].clientX - sliderRect.left;
                } else {
                    initialX = e.clientX - sliderRect.left;
                }

                // Set the initial position of the draggable circle
                this.draggableCircle.style.left = `${initialX}px`;
            }


            let clonedE = {
                ...e
            };
            clonedE.type = 'touchmove';
            clonedE.touches = e.touches;
            // document.querySelector(".touch_info").innerHTML=e.which;
            this.handleDragging(clonedE);
        }

        // Add event listeners for dragging
        document.addEventListener('mousemove', this.handleDragging.bind(this));
        document.addEventListener('mouseup', this.stopDragging.bind(this));
        document.addEventListener('touchmove', this.handleDragging.bind(this), {
            passive: false
        });
        document.addEventListener('touchend', this.stopDragging.bind(this));
    }

    // Handle dragging event for joystick control
    handleDragging(e) {
        e.preventDefault && e.preventDefault();
        e.stopImmediatePropagation && e.stopImmediatePropagation();

        if (this.isDragging) {
            const sliderRect = this.sliderContainer.getBoundingClientRect();

            sliderTouchIndex = Math.min(e.touches.length, sliderTouchIndex)
            if (Global.controlType == "gyro") {
                if (e.type === 'touchmove') {
                    newY = e.touches[sliderTouchIndex - 1].clientY - sliderRect.top;
                } else {
                    newY = e.clientY - sliderRect.top;
                }
                newY = Math.max(0, Math.min(newY, sliderRect.height));
                const center = sliderRect.height / 2;
                const dragAmount = -1 * ((newY - center) / center);
                // Dispatch joystick control events based on drag direction
                if (dragAmount > 0) {
                    this.events.dispatchEvent({
                        type: "GAME_EVENTS",
                        message: {
                            "event_type": "joystick_control_add",
                            data: {
                                "code": "ArrowUp",
                                "force": Math.abs(dragAmount)
                            }
                        }
                    });
                    this.events.dispatchEvent({
                        type: "GAME_EVENTS",
                        message: {
                            "event_type": "joystick_control_remove",
                            data: {
                                "code": "ArrowDown"
                            }
                        }
                    });
                } else {
                    this.events.dispatchEvent({
                        type: "GAME_EVENTS",
                        message: {
                            "event_type": "joystick_control_add",
                            data: {
                                "code": "ArrowDown",
                                "force": Math.abs(dragAmount)
                            }
                        }
                    });
                    this.events.dispatchEvent({
                        type: "GAME_EVENTS",
                        message: {
                            "event_type": "joystick_control_remove",
                            data: {
                                "code": "ArrowUp"
                            }
                        }
                    });
                }

                this.draggableCircle.style.top = `${newY}px`;
            } else {
                if (e.type === 'touchmove') {
                    newX = e.touches[sliderTouchIndex - 1].clientX - sliderRect.left;
                } else {
                    newX = e.clientX - sliderRect.left;
                }
                newX = Math.max(0, Math.min(newX, sliderRect.width));
                const center = sliderRect.width / 2;
                const dragAmount = -1 * ((newX - center) / center);
                this.draggableCircle.style.left = `${newX}px`;
                // document.querySelector(".touch_info").innerHTML= dragAmount;

                this.events.dispatchEvent({
                    type: "GAME_EVENTS",
                    message: {
                        "event_type": "gyro_control_remove",
                        data: {
                            "code": "ArrowLeft"
                        }
                    }
                });
                this.events.dispatchEvent({
                    type: "GAME_EVENTS",
                    message: {
                        "event_type": "gyro_control_remove",
                        data: {
                            "code": "ArrowRight"
                        }
                    }
                });
                if (dragAmount > 0) {

                    this.events.dispatchEvent({
                        type: "GAME_EVENTS",
                        message: {
                            "event_type": "gyro_control_add",
                            data: {
                                "code": "ArrowLeft",
                                "force": Math.abs(Math.pow(dragAmount, 1.0))
                            }
                        }
                    });
                    /* this.events.dispatchEvent({
                        type: "GAME_EVENTS",
                        message: {
                            "event_type": "gyro_control_remove",
                            data: {
                                "code": "ArrowRight"
                            }
                        }
                    }); */
                } else if (dragAmount < 0) {

                    this.events.dispatchEvent({
                        type: "GAME_EVENTS",
                        message: {
                            "event_type": "gyro_control_add",
                            data: {
                                "code": "ArrowRight",
                                "force": Math.abs(Math.pow(dragAmount, 1.0))
                            }
                        }
                    });
                    /*  this.events.dispatchEvent({
                         type: "GAME_EVENTS",
                         message: {
                             "event_type": "gyro_control_remove",
                             data: {
                                 "code": "ArrowLeft"
                             }
                         }
                     }); */
                } else {
                    /* this.events.dispatchEvent({
                        type: "GAME_EVENTS",
                        message: {
                            "event_type": "gyro_control_remove",
                            data: {
                                "code": "ArrowRight"
                            }
                        }
                    });
                    this.events.dispatchEvent({
                        type: "GAME_EVENTS",
                        message: {
                            "event_type": "gyro_control_remove",
                            data: {
                                "code": "ArrowLeft"
                            }
                        }
                    }); */
                }


                /* 
                
                // Dispatch joystick control events based on drag direction
            if (dragAmount > 0) {
                this.events.dispatchEvent({
                    type: "GAME_EVENTS",
                    message: {
                        "event_type": "joystick_control_add",
                        data: {
                            "code": "ArrowUp",
                            "force": Math.abs(dragAmount)
                        }
                    }
                });
                this.events.dispatchEvent({
                    type: "GAME_EVENTS",
                    message: {
                        "event_type": "joystick_control_remove",
                        data: {
                            "code": "ArrowDown"
                        }
                    }
                });
            } else {
                this.events.dispatchEvent({
                    type: "GAME_EVENTS",
                    message: {
                        "event_type": "joystick_control_add",
                        data: {
                            "code": "ArrowDown",
                            "force": Math.abs(dragAmount)
                        }
                    }
                });
                this.events.dispatchEvent({
                    type: "GAME_EVENTS",
                    message: {
                        "event_type": "joystick_control_remove",
                        data: {
                            "code": "ArrowUp"
                        }
                    }
                });
            }

            this.draggableCircle.style.top = `${newY}px`;
                */
            }


            // Keep the draggable circle within the slider limits





        }
    }

    // Stop dragging event for joystick control
    stopDragging(e) {
        if (e &&
            (e.target === 'accelarator' ||
                e.target === 'brake')
        ) return false;


        if (this.isDragging) {
            this.isDragging = false;
            if (Global.controlType == "gyro") {
                this.events.dispatchEvent({
                    type: "GAME_EVENTS",
                    message: {
                        "event_type": "joystick_control_remove",
                        data: {
                            "code": "ArrowDown"
                        }
                    }
                });
                this.events.dispatchEvent({
                    type: "GAME_EVENTS",
                    message: {
                        "event_type": "joystick_control_remove",
                        data: {
                            "code": "ArrowUp"
                        }
                    }
                });
            } else {
                this.events.dispatchEvent({
                    type: "GAME_EVENTS",
                    message: {
                        "event_type": "gyro_control_remove",
                        data: {
                            "code": "ArrowRight"
                        }
                    }
                });
                this.events.dispatchEvent({
                    type: "GAME_EVENTS",
                    message: {
                        "event_type": "gyro_control_remove",
                        data: {
                            "code": "ArrowLeft"
                        }
                    }
                });
            }
            // Dispatch joystick control remove events


            // Reset draggable circle to center
            this.resetToCenter();

            // Remove event listeners for dragging
            document.removeEventListener('mousemove', this.handleDragging);
            document.removeEventListener('mouseup', this.stopDragging);
            document.removeEventListener('touchmove', this.handleDragging);
            document.removeEventListener('touchend', this.stopDragging);
        }
    }

    // Reset draggable circle to center
    resetToCenter() {
        const sliderRect = this.sliderContainer.getBoundingClientRect();
        this.draggableCircle.style.top = `${sliderRect.height / 2}px`;
        this.draggableCircle.style.left = `${sliderRect.width / 2}px`;
    }
    applyAccelarator(e) {
        // document.querySelector(".touch_info").innerHTML= e.changedTouches.length;
        e.preventDefault();
        e.stopImmediatePropagation();

        document.querySelector("#accelarator .hover").classList.add("active");
        this.events.dispatchEvent({
            type: "GAME_EVENTS",
            message: {
                "event_type": "joystick_control_add",
                data: {
                    "code": "ArrowUp",
                    "force": 1
                }
            }
        });
    }
    removeAccelarator(e) {
        e.preventDefault();
        e.stopImmediatePropagation();

        document.querySelector("#accelarator .hover").classList.remove("active");
        this.events.dispatchEvent({
            type: "GAME_EVENTS",
            message: {
                "event_type": "joystick_control_remove",
                data: {
                    "code": "ArrowUp",
                }
            }
        });
    }
    applyBrake(e) {
        e.preventDefault();
        e.stopImmediatePropagation();

        document.querySelector("#brake .hover").classList.add("active");
        this.events.dispatchEvent({
            type: "GAME_EVENTS",
            message: {
                "event_type": "joystick_control_add",
                data: {
                    "code": "ArrowDown",
                    "force": 1
                }
            }
        });
    }
    removeBrake(e) {
        e.preventDefault();
        e.stopImmediatePropagation();

        document.querySelector("#brake .hover").classList.remove("active");
        this.events.dispatchEvent({
            type: "GAME_EVENTS",
            message: {
                "event_type": "joystick_control_remove",
                data: {
                    "code": "ArrowDown"
                }
            }
        });
    }
    addBrakeSteeringAndAccelarator() {
        document.querySelector("#accelarator").addEventListener("touchstart", this.applyAccelarator);
        document.querySelector("#accelarator").addEventListener("touchend", this.removeAccelarator);

        document.querySelector("#brake").addEventListener("touchstart", this.applyBrake);
        document.querySelector("#brake").addEventListener("touchend", this.removeBrake);

        /* 
         e.preventDefault();
        e.stopImmediatePropagation();
        */
        document.querySelector(".steering_left").addEventListener("touchstart", (e)=>{
            e.preventDefault();
            e.stopImmediatePropagation();
            this.onKeyDown.call(this, {
                'code': 'ArrowLeft'
            })
        });
        document.querySelector(".steering_left").addEventListener("touchend", (e)=>{
            e.preventDefault();
            e.stopImmediatePropagation();
            this.onKeyUp.call(this, {
                'code': 'ArrowLeft'
            })
        });

        document.querySelector(".steering_right").addEventListener("touchstart", (e)=>{
            e.preventDefault();
            e.stopImmediatePropagation();
            this.onKeyDown.call(this, {
                'code': 'ArrowRight'
            })
        });
        document.querySelector(".steering_right").addEventListener("touchend", (e)=>{
            e.preventDefault();
            e.stopImmediatePropagation();
            this.onKeyUp.call(this, {
                'code': 'ArrowRight'
            })
        });
    }
    addTurnJoystick() {
        // this.sliderContainer = document.getElementById('slider-turn-container');
        // this.draggableCircle = document.getElementById('turn_draggable-circle');
        // this.draggableArea = document.getElementById("turn_draggable_area");

        // // Add event listeners for dragging on joystick and area
        // this.draggableCircle.addEventListener('mousedown', this.startDragging.bind(this, true));
        // this.draggableCircle.addEventListener('touchstart', this.startDragging.bind(this, true));
        // this.draggableArea.addEventListener('mousedown', this.startDragging.bind(this, false));
        // this.draggableArea.addEventListener('touchstart', this.startDragging.bind(this, false));
    }
    // Add joystick to the scene
    addSpeedJoystick() {
        this.sliderContainer = document.getElementById('slider-container');
        this.draggableCircle = document.getElementById('draggable-circle');
        this.draggableArea = document.getElementById("draggable_area");

        // Add event listeners for dragging on joystick and area
        this.draggableCircle.addEventListener('mousedown', this.startDragging.bind(this, true));
        this.draggableCircle.addEventListener('touchstart', this.startDragging.bind(this, true));
        this.draggableArea.addEventListener('mousedown', this.startDragging.bind(this, false));
        this.draggableArea.addEventListener('touchstart', this.startDragging.bind(this, false));
    }


    // Handle various game events
    onGameEvents(data) {
        switch (data['message']['event_type']) {

            case "replay_game":
                this.replayGame();
                break;
            case "ask_permission":
                this.permission();
                break;
            case "gyro_control_add":
            case "joystick_control_add":
                this.onKeyDown(data['message']['data']);
                break;
            case "gyro_control_remove":
            case "joystick_control_remove":
                this.onKeyUp(data['message']['data']);
                break;
            case "load_complete":
                this.onAssetsLoadComplete();
                break;
        }
    }

    // Handle events after assets are loaded
    onAssetsLoadComplete() {
        // let startBtn = document.querySelector("#gameStart");
        // startBtn.addEventListener("click", this.permission.bind(this));
    }

    // Replay the game
    replayGame() {
        this.gameOver = false;
        Global.gameOver = false;
        this.gameStarted = true;
    }

    // Request permission for device motion access
    permission() {
        if (typeof (DeviceMotionEvent) !== "undefined" && typeof (DeviceMotionEvent.requestPermission) === "function") {
            DeviceMotionEvent.requestPermission()
                .then(response => {
                    if (response == "granted") {
                        this.onControlAccess(true, true);
                    } else {
                        this.onControlAccess(false, true);
                    }
                })
                .catch(console.error)
        } else {
            this.onControlAccess(isMobile, isMobile);
        }
    }
    startGame() {
        this.events.dispatchEvent({
            type: "GAMESCENE_EVENTS",
            message: {
                "event_type": "start_game",
                data: {}
            }
        });
    }
    // Handle control access
    onControlAccess(isGyro, showAccelerationUI) {
        Global.isGyro = isGyro;

        /* let startBtn = document.querySelector("#gameStart");
        startBtn.remove(); */

        // Dispatch start game event
        /* this.events.dispatchEvent({
            type: "GAMESCENE_EVENTS",
            message: {
                "event_type": "start_game",
                data: {}
            }
        });
 */
        // Show acceleration UI if applicable

        this.showAccelerationUI = showAccelerationUI;
        /*   if (showAccelerationUI) {
             
          } */


        if (isGyro) {
            this.events.dispatchEvent({
                type: "PLAYER_EVENTS",
                message: {
                    "event_type": "update_gyro_factor",
                    data: {
                        'factor': GYRO_FACTOR
                    }
                }
            });
            // Add device orientation event listener for gyro control
            if (!Global.deviceMotionTracking) {
                Global.deviceMotionTracking = true;
                window.addEventListener('deviceorientation', this.handleOrientationChange);
            }
        }
        this.events.dispatchEvent({
            type: "GAME_EVENTS",
            message: {
                "event_type": "show_counter",
                data: {}
            }
        });
        // this.startGame();
        // Set flag for saving initial directions
        this.readyToSaveDirection = true;
    }

    // Handle general events
    onEvents(data) {
        switch (data['message']['event_type']) {
            case "start_game":
                this.gameStarted = true;
                this.gameOver = false;
                this.directions = [];
                if (Global.controlType == "gyro") {
                    document.querySelector("#slider-container").classList.add("active");
                    // this.addSpeedJoystick();
                } else {
                    if (isMobile) {
                        // document.querySelector("#slider-turn-container").classList.add("active");
                        document.querySelector(".joystick_container").classList.add("active");

                        // this.addTurnJoystick();
                        this.addBrakeSteeringAndAccelarator();
                    }
                }
                break;
            case "update":
                this.update();
                break;
            case "game_over":
            case "disable_control":
                this.gameOver = true;
                Global.gameOver = true;
                this.gameStarted = false;
                this.stopDragging();
                this.directions = [];
                document.querySelector("#slider-container").classList.remove("active");
                // document.querySelector("#slider-turn-container").classList.remove("active");
                document.querySelector(".joystick_container").classList.remove("active");
                break;
        }
    }
    animateSteering(rotate) {
        // const _item= document.querySelector(`.steering`);
        // gsap.killTweensOf(_item);

        // gsap.to(_item, {
        //     rotate,
        //     yoyo: true,
        //     duration:0.35,

        // })
    }
    // Handle key down events
    onKeyDown(event) {
        const requiredCodes=[
            "ArrowUp","ArrowLeft","ArrowRight","ArrowDown","KeyA","KeyW","KeyS","KeyD"
        ]
        const registerActive= document.querySelector("#register_sec").classList.contains("active")
        if(event && !registerActive &&  event.preventDefault){
            event.preventDefault();
        }
       
        let leftFound = false;
        let rightFound = false;
        let upFound = false;
        let downFound = false;
        let leftIndex = -1;
        let rightIndex = -1;
        let upIndex = -1;
        let downIndex = -1;

        // Check for existing direction and update force if necessary
        this.directions.forEach((direction, index) => {
            if (direction['direction'] == 'left') {
                leftFound = true;
                leftIndex = index;
            }
            if (direction['direction'] == 'right') {
                rightFound = true;
                rightIndex = index;
            }
            if (direction['direction'] == 'up') {
                upFound = true;
                upIndex = index;
            }
            if (direction['direction'] == 'down') {
                downFound = true;
                downIndex = index;
            }
        })

        // Add new direction if not found, or update force if found
        if ((event.code == "ArrowLeft" || event.code == "KeyA") && !leftFound) {
            document.querySelector(".steering_left .hover").classList.add("active");
            // this.animateSteering(`-20deg`);
            this.directions.push({
                'direction': 'left',
                'force': event.force || 1
            });
        } else if ((event.code == "ArrowRight" || event.code == "KeyD") && !rightFound) {
            document.querySelector(".steering_right .hover").classList.add("active");
            // this.animateSteering(`20deg`);
            this.directions.push({
                'direction': 'right',
                'force': event.force || 1
            });
        }

        if ((event.code == "ArrowUp" || event.code == "KeyW") && !upFound) {
            this.directions.push({
                'direction': 'up',
                'force': event.force || 1
            });
        } else if ((event.code == "ArrowDown" || event.code == "KeyS") && !downFound) {
            this.directions.push({
                'direction': 'down',
                'force': event.force || 1
            });
        }

        // Update force if direction already exists
        if ((event.code == "ArrowLeft" || event.code == "KeyA") && leftFound) {
            this.directions[leftIndex]['force'] = event.force || 1;
        } else if ((event.code == "ArrowRight" || event.code == "KeyD") && rightFound) {
            this.directions[rightIndex]['force'] = event.force || 1;
        }

        if ((event.code == "ArrowUp" || event.code == "KeyW") && upFound) {
            this.directions[upIndex]['force'] = event.force || 1;
        } else if ((event.code == "ArrowDown" || event.code == "KeyS") && downFound) {
            this.directions[downIndex]['force'] = event.force || 1;
        }
    };

    // Handle key up events
    onKeyUp(event) {
        if(event && event.preventDefault && !this.gameOver){
            event.preventDefault();
        }
        let leftIndex = -1;
        let rightIndex = -1;
        let upIndex = -1;
        let downIndex = -1;



        // Find and remove direction on key up
        this.directions.forEach((direction, index) => {
            if (direction['direction'] == 'left') {
                leftIndex = index;
            }
            if (direction['direction'] == 'right') {
                rightIndex = index;
            }
            if (direction['direction'] == 'up') {
                upIndex = index;
            }
            if (direction['direction'] == 'down') {
                downIndex = index;
            }
        })

        if ((event.code == "ArrowLeft" || event.code == "KeyA") && leftIndex !== -1) {
            document.querySelector(".steering_left .hover").classList.remove("active");
            // this.animateSteering(`0deg`);
            this.directions.splice(leftIndex, 1);
        } else if ((event.code == "ArrowRight" || event.code == "KeyD") && rightIndex !== -1) {
            this.directions.splice(rightIndex, 1);
            document.querySelector(".steering_right .hover").classList.remove("active");
            // this.animateSteering(`0deg`);
        }

        if ((event.code == "ArrowUp" || event.code == "KeyW") && upIndex !== -1) {
            this.directions.splice(upIndex, 1);
        } else if ((event.code == "ArrowDown" || event.code == "KeyS") && downIndex !== -1) {
            this.directions.splice(downIndex, 1);
        }
    };

    // Update method for handling forces and dispatching events
    update(dt) {
        let dir = null;
        let forceFactor = 0;
        let prevDir = null;
        let dirnForceApplied = false;
        let forceY = 0;
        let dirnCnt = 0;

        // Iterate through directions and update forces
        if (this.directions.length > 0 && !this.gameOver) {
            for (let i = this.directions.length - 1; i >= 0; i--) {
                if (dirnCnt >= 2) return false;
                dir = this.directions[i]['direction'];
                forceFactor = this.directions[i]['force'];

                // Apply forces based on direction
                if ((dir == "left" || dir == "right") && (prevDir != "left" && prevDir != "right")) {
                    dirnForceApplied = true;
                    if ((Global.controlType == "gyro" || !isMobile)) {
                        if (Math.abs(this.forceX) <= FORCE_X_LIMIT * forceFactor * 1)
                            this.forceX += (dir == "right" ? -1 * FORCE_X_FACT : dir == "left" ? FORCE_X_FACT : 0) * forceFactor * Global.delta;


                    } else {
                        this.forceX = new THREE.Vector3(this.lastForceX, 0, 0).lerp(new THREE.Vector3((dir == "right" ? -1 * FORCE_X_FACT : dir == "left" ? FORCE_X_FACT : 0) * forceFactor * (50 * Math.pow(forceFactor, 1)), 0, 0), FORCE_X_LERP * Global.delta * .5).x;


                    }

                    this.lastForceX = this.forceX;


                }

                if ((dir == "up" || dir == "down") && (prevDir != "up" && prevDir != "down")) {
                    forceY = (dir == "up" ? FORCE_Y_U_FACT : dir == "down" ? FORCE_Y_D_FACT : 0) * forceFactor * Global.delta;
                }

                dirnCnt++;
                prevDir = dir;
                this.lastDir = dir;
            }
        }

        // If no direction force applied, lerp forceX to 0
        if (!dirnForceApplied) {
            this.forceX = new THREE.Vector3(this.lastForceX, 0, 0).lerp(new THREE.Vector3(0, 0, 0), FORCE_X_LERP * Global.delta).x;
            this.lastForceX = this.forceX;
        }

        // Dispatch events for steering and reset forces

        this.events.dispatchEvent({
            type: "GAMECONTROL_EVENTS",
            message: {
                "event_type": "steer",
                "data": {
                    "forceX": this.forceX,
                    forceY,
                    "directionApplied": dirnForceApplied,
                    'rotate_wheel': true
                }
            }
        });

        this.events.dispatchEvent({
            type: "GAMECONTROL_EVENTS",
            message: {
                "event_type": "reset_force",
                "data": {
                    'forceXActive': (this.directions.length > 0 ? (this.directions[this.directions.length - 1]['direction'] == 'left' || this.directions[this.directions.length - 1]['direction'] == 'right') : false),
                    'forceYActive': (this.directions.length > 0 ? (this.directions[this.directions.length - 1]['direction'] == 'up' || this.directions[this.directions.length - 1]['direction'] == 'down') : false)
                }
            }
        });
    };
}